import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Plus, Trash2, Check, Clock, AlertTriangle, AlertCircle,
  CalendarDays, Edit, Mail, RefreshCw, FileText, HeartHandshake,
  Home, Heart, Package, Truck, Award, UserPlus, Car, Filter
} from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { NoteIconButton } from './NotesModal';
import { toast } from 'sonner';
import api from '../../services/api';

// Badge configuration for display - includes Driver
const BADGE_CONFIG = {
  cleaner: { icon: Home, color: 'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30', label: 'Cleaner' },
  private_concierge: { icon: Heart, color: 'bg-[#F4C2C2]/15 text-purple-700 border-[#F4C2C2]/30', label: 'Concierge' },
  social_engagement: { icon: Heart, color: 'bg-pink-100 text-pink-700 border-pink-200', label: 'Social' },
  errand_runner: { icon: Package, color: 'bg-amber-100 text-amber-700 border-[var(--warning)]/30', label: 'Errand Runner' },
  courier: { icon: Truck, color: 'bg-[var(--success)]/15 text-green-700 border-[var(--success)]/30', label: 'Courier' },
  driver: { icon: Car, color: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Driver' }
};

const WorkersTab = ({
  workers,
  bulkSelectMode,
  setBulkSelectMode,
  selectedWorkerIds,
  setSelectedWorkerIds,
  setShowAddWorkerModal,
  handleBulkDeleteWorkers,
  handleOpenWorkerSchedule,
  handleEditWorker,
  handleSendOffer,
  handleViewWorkerFile,
  handleDeleteWorker,
  noteCounts,
  setNotesModal,
  refreshWorkers
}) => {
  const [addingOwnerAsWorker, setAddingOwnerAsWorker] = useState(false);
  const [roleFilter, setRoleFilter] = useState('all'); // all, workers, drivers
  
  // Filter workers based on role
  const filteredWorkers = workers.filter(w => {
    if (roleFilter === 'drivers') return w.is_driver === true;
    if (roleFilter === 'workers') return w.is_driver !== true;
    return true;
  });
  
  const driverCount = workers.filter(w => w.is_driver).length;
  const workerCount = workers.filter(w => !w.is_driver).length;
  
  const toggleWorkerSelection = (workerId) => {
    setSelectedWorkerIds(prev => 
      prev.includes(workerId) 
        ? prev.filter(id => id !== workerId)
        : [...prev, workerId]
    );
  };
  
  const handleAddOwnerAsWorker = async () => {
    setAddingOwnerAsWorker(true);
    try {
      const res = await api.post('/workers/owner-as-worker');
      toast.success(res.data.message);
      if (refreshWorkers) refreshWorkers();
    } catch (error) {
      const msg = error.response?.data?.detail || error.response?.data?.message || 'Failed to add owner as worker';
      toast.error(msg);
    } finally {
      setAddingOwnerAsWorker(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Workers & Drivers</h1>
        <div className="flex items-center gap-3">
          {/* Bulk Select Toggle */}
          <Button 
            variant={bulkSelectMode.workers ? "default" : "outline"}
            onClick={() => {
              setBulkSelectMode(prev => ({ ...prev, workers: !prev.workers }));
              setSelectedWorkerIds([]);
            }}
            className={bulkSelectMode.workers ? "bg-rose-600 text-white" : ""}
            data-testid="workers-bulk-select-toggle"
          >
            {bulkSelectMode.workers ? "Cancel Selection" : "Select Multiple"}
          </Button>
          {bulkSelectMode.workers && selectedWorkerIds.length > 0 && (
            <Button 
              onClick={handleBulkDeleteWorkers}
              className="bg-[var(--error)] hover:bg-[var(--error)]/80 text-white"
              data-testid="workers-bulk-delete"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete ({selectedWorkerIds.length})
            </Button>
          )}
          <Button onClick={() => setShowAddWorkerModal(true)} className="btn-primary" data-testid="add-worker-btn">
            <Plus className="w-5 h-5 mr-2" />
            Add Worker
          </Button>
          <Button 
            onClick={handleAddOwnerAsWorker} 
            variant="outline" 
            disabled={addingOwnerAsWorker}
            className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
            data-testid="add-owner-as-worker-btn"
          >
            <UserPlus className="w-5 h-5 mr-2" />
            {addingOwnerAsWorker ? 'Adding...' : 'Add Me as Worker'}
          </Button>
        </div>
      </div>
      
      {/* Role Filter Tabs */}
      <div className="flex items-center gap-2 mb-6">
        <Filter className="w-4 h-4 text-[var(--text-muted)]" />
        <div className="flex rounded-lg overflow-hidden border border-[var(--border-primary)]">
          <button
            onClick={() => setRoleFilter('all')}
            className={`px-4 py-2 text-sm font-medium transition-all ${
              roleFilter === 'all' 
                ? 'bg-[var(--primary)] text-white' 
                : 'bg-[var(--background)] hover:bg-[var(--background-subtle)]'
            }`}
            data-testid="filter-all"
          >
            All ({workers.length})
          </button>
          <button
            onClick={() => setRoleFilter('workers')}
            className={`px-4 py-2 text-sm font-medium transition-all border-l border-[var(--border-primary)] ${
              roleFilter === 'workers' 
                ? 'bg-[#D4AF37] text-white' 
                : 'bg-[var(--background)] hover:bg-[var(--background-subtle)]'
            }`}
            data-testid="filter-workers"
          >
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" /> Workers ({workerCount})
            </span>
          </button>
          <button
            onClick={() => setRoleFilter('drivers')}
            className={`px-4 py-2 text-sm font-medium transition-all border-l border-[var(--border-primary)] ${
              roleFilter === 'drivers' 
                ? 'bg-blue-600 text-white' 
                : 'bg-[var(--background)] hover:bg-[var(--background-subtle)]'
            }`}
            data-testid="filter-drivers"
          >
            <span className="flex items-center gap-1">
              <Car className="w-4 h-4" /> Drivers ({driverCount})
            </span>
          </button>
        </div>
      </div>
      
      {/* Select All / Clear */}
      {bulkSelectMode.workers && workers.length > 0 && (
        <div className="mb-4 flex items-center gap-4 p-3 bg-rose-50 rounded-lg">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setSelectedWorkerIds(workers.map(w => w.id))}
          >
            Select All ({workers.length})
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setSelectedWorkerIds([])}
          >
            Clear Selection
          </Button>
          <span className="text-sm text-rose-600 ml-auto">
            {selectedWorkerIds.length} selected
          </span>
        </div>
      )}
      
      {filteredWorkers.length === 0 ? (
        <div className="card p-12 text-center">
          <Users className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
          <p className="text-[var(--text-secondary)] mb-4">
            {roleFilter === 'drivers' ? 'No drivers yet. Add drivers from the Rides tab or mark existing workers as drivers.' : 
             roleFilter === 'workers' ? 'No workers yet (excluding drivers). Add workers or approve applications.' :
             'No workers yet. Add workers or approve applications.'}
          </p>
          <Button onClick={() => setShowAddWorkerModal(true)} className="btn-primary">
            <Plus className="w-5 h-5 mr-2" />
            Add Worker Manually
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredWorkers.map(worker => (
            <div key={worker.id} className={`card p-6 relative ${bulkSelectMode.workers && selectedWorkerIds.includes(worker.id) ? 'ring-2 ring-rose-500 bg-rose-50' : ''}`} data-testid={`worker-card-${worker.id}`}>
              {/* Bulk Select Checkbox */}
              {bulkSelectMode.workers && (
                <div className="absolute top-3 right-3">
                  <Checkbox
                    checked={selectedWorkerIds.includes(worker.id)}
                    onCheckedChange={() => toggleWorkerSelection(worker.id)}
                    className="w-5 h-5"
                  />
                </div>
              )}
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-[var(--accent)] rounded-full flex items-center justify-center overflow-hidden">
                  {worker.profile_picture ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${worker.profile_picture}`} alt={worker.full_name} className="w-full h-full object-cover" />
                  ) : (
                    <Users className="w-7 h-7 text-[var(--primary)]" />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold">{worker.full_name}</h3>
                    {worker.employee_id && (
                      <span className="px-2 py-0.5 bg-[var(--primary)]/10 text-[var(--primary)] text-xs font-mono rounded">
                        {worker.employee_id}
                      </span>
                    )}
                    {worker.is_verified && (
                      <span className="w-5 h-5 bg-[var(--success)] rounded-full flex items-center justify-center" title="Verified">
                        <Check className="w-3 h-3 text-white" />
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[var(--text-muted)]">{worker.email}</p>
                  {/* Non-Medical Services Badge */}
                  <div className="mt-1 flex flex-wrap gap-1">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded border border-emerald-200" title="This worker provides non-medical services only">
                      <HeartHandshake className="w-3 h-3" />
                      Non-Medical Only
                    </span>
                    {/* Driver Badge */}
                    {worker.is_driver && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded border border-blue-200" title="This worker is a driver">
                        <Car className="w-3 h-3" />
                        Driver
                      </span>
                    )}
                  </div>
                  {/* Service Badges */}
                  {worker.service_badges && worker.service_badges.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {worker.service_badges.map(badge => {
                        const config = BADGE_CONFIG[badge.badge_id];
                        if (!config) return null;
                        return (
                          <span 
                            key={badge.badge_id}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border ${config.color}`}
                            title={`${config.label} - ${badge.earned_via === 'course' ? 'Earned via course' : 'Manually assigned'}`}
                          >
                            <config.icon className="w-3 h-3" />
                            {config.label}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Offer Letter Status Badge */}
              {worker.offer_letter_status && worker.offer_letter_status !== 'signed' && (
                <div className={`mb-3 p-2 rounded-lg text-xs flex items-center gap-2 ${
                  worker.offer_letter_status === 'awaiting_rate' 
                    ? 'bg-[var(--warning)]/10 text-amber-700 border border-[var(--warning)]/30'
                    : worker.offer_letter_status === 'pending'
                      ? 'bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30'
                      : 'bg-[var(--background-subtle)] text-[var(--text-secondary)]'
                }`}>
                  {worker.offer_letter_status === 'awaiting_rate' && (
                    <>
                      <AlertTriangle className="w-3 h-3" />
                      Set pay rate to send offer letter
                    </>
                  )}
                  {worker.offer_letter_status === 'pending' && (
                    <>
                      <Clock className="w-3 h-3" />
                      Awaiting offer letter signature
                    </>
                  )}
                </div>
              )}
              
              {/* Login Status */}
              {!worker.login_enabled && (
                <div className="mb-3 p-2 rounded-lg text-xs bg-[var(--error)]/10 text-red-700 border border-[var(--error)]/30 flex items-center gap-2">
                  <AlertCircle className="w-3 h-3" />
                  Login disabled (pending offer signature)
                </div>
              )}
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[var(--text-muted)]">Phone:</span>
                  <span>{worker.phone}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text-muted)]">Services:</span>
                  <span>{worker.service_types.map(t => t === 'rides' ? 'Rides' : t === 'errands' ? 'Errands' : t === 'courier' ? 'Courier' : t === 'laundry' ? 'Laundry' : t).join(', ')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text-muted)]">Pay Rate:</span>
                  <span className={`font-bold ${worker.hourly_rate ? 'text-[var(--primary)]' : 'text-amber-600'}`}>
                    {worker.hourly_rate ? `$${worker.hourly_rate}/hr` : 'Not set'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text-muted)]">Rating:</span>
                  <span className="text-[var(--warning)]">{worker.rating} ★</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text-muted)]">Jobs Completed:</span>
                  <span>{worker.total_jobs}</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-[var(--border-primary)] flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className={`badge-${worker.login_enabled ? (worker.is_active ? 'success' : 'warning') : 'error'}`}>
                    {worker.login_enabled 
                      ? (worker.is_active ? 'Active' : 'Inactive')
                      : 'Pending Onboard'
                    }
                  </span>
                  <NoteIconButton
                    entityType="worker"
                    entityId={worker.id}
                    entityName={worker.full_name}
                    noteCount={noteCounts[`worker_${worker.id}`] || 0}
                    onOpen={(type, id, name) => setNotesModal({ open: true, entityType: type, entityId: id, entityName: name })}
                  />
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleOpenWorkerSchedule(worker)}
                    data-testid={`view-schedule-${worker.id}`}
                  >
                    <CalendarDays className="w-4 h-4 mr-1" />
                    Schedule
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleEditWorker(worker)}
                    data-testid={`edit-worker-${worker.id}`}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  {worker.hourly_rate && (!worker.offer_letter_status || worker.offer_letter_status === 'awaiting_rate') && (
                    <Button 
                      size="sm"
                      className="bg-[#D4AF37]/100 hover:bg-[#E6C965] text-white"
                      onClick={() => handleSendOffer(worker.id, worker.full_name)}
                      data-testid={`send-offer-${worker.id}`}
                    >
                      <Mail className="w-4 h-4 mr-1" />
                      Send Offer
                    </Button>
                  )}
                  {worker.offer_letter_status === 'pending' && (
                    <Button 
                      size="sm"
                      variant="outline"
                      className="text-amber-600 border-[var(--warning)]/30 hover:bg-[var(--warning)]/10"
                      onClick={() => handleSendOffer(worker.id, worker.full_name, true)}
                      data-testid={`resend-offer-${worker.id}`}
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Resend Offer
                    </Button>
                  )}
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-[#F4C2C2] border-[#F4C2C2]/30 hover:bg-[#F4C2C2]/10"
                    onClick={() => handleViewWorkerFile(worker.id)}
                    data-testid={`view-file-${worker.id}`}
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    View File
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-[var(--error)] border-[var(--error)]/30 hover:bg-[var(--error)]/10"
                    onClick={() => handleDeleteWorker(worker.id)}
                    data-testid={`delete-worker-${worker.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default WorkersTab;
